import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { saveRecord, getAllRecords, fmtDate } from '../utils/storage';
import { fetchLoginRecord } from '../utils/userAccessApi';
import { verifyAccessKey } from '../utils/accessKey';
import { saveSessionExamIds, clearSessionExamIds, saveSessionSetLimit } from '../data/examCatalog';
import { ExamSubmission } from '../types';

const LoginPage: React.FC = () => {
  const [redirecting, setRedirecting] = useState(true);
  const [username, setUsername] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; key?: string }>({});
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [dashUser, setDashUser] = useState('');
  const { msg, type, visible, showToast } = useToast();
  const location = useLocation();

  const validate = () => {
    const errs: { username?: string; key?: string } = {};
    if (!username.trim() || username.trim().length < 3) errs.username = 'Username is required (min 3 chars).';
    if (!accessKey.trim() || accessKey.trim().length < 4) errs.key = 'Access key is required (min 4 chars).';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) { showToast('⚠️ Please fill in all required fields.', 'error'); return; }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Look the user up, then verify the access key against the stored salt:hash
      const record = await fetchLoginRecord(username.trim());

      if (!record.found || !record.accessKey) {
        showToast('⚠️ Invalid username or access key.', 'error');
        setAccessKey('');
        return;
      }

      if (await verifyAccessKey(accessKey.trim(), record.accessKey)) {
        saveRecord('access_requests', { type: 'login_attempt', username: username.trim(), source: 'login_page' });
        setDashUser(username.trim());
        setLoggedIn(true);
        sessionStorage.setItem('userLoggedIn', '1');
        sessionStorage.setItem('dashUser', username.trim());
        // Exams assigned to this user in the admin panel decide which cards the dashboard shows
        saveSessionExamIds(record.examIds || []);
        saveSessionSetLimit(record.noOfSets ?? null);

        // Redirect to the intended page or dashboard
        const from = location.state?.from || '/dashboard';
        window.location.href = from;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        showToast('⚠️ Invalid username or access key.', 'error');
        setAccessKey('');
      }
    } catch (err) {
      console.error('Login error:', err);
      showToast('⚠️ Invalid username or access key.', 'error');
      setAccessKey('');
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    sessionStorage.removeItem('userLoggedIn');
    sessionStorage.removeItem('dashUser');
    clearSessionExamIds();
    setLoggedIn(false);
    setUsername('');
    setAccessKey('');
    setErrors({});
  };

  const exams = getAllRecords('exam_submissions') as unknown as ExamSubmission[];

  return (
    <Layout>
      <Toast msg={msg} type={type} visible={visible} />

      {!loggedIn ? (
        <div style={{ minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(140deg, var(--blue-pale) 0%, #fff 50%, var(--orange-pale) 100%)', padding: '40px 5%' }}>
          <div style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '44px 40px', maxWidth: '420px', width: '100%', boxShadow: '0 12px 40px rgba(37,99,235,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.2rem', color: 'var(--blue)', marginBottom: '28px', justifyContent: 'center' }}>
              <div className="logo-mark">360</div>
              <span style={{ color: 'var(--gray-800)' }}><span style={{ color: 'var(--blue)' }}>Digi</span><span style={{ color: 'var(--orange)' }}>Learning</span></span>
            </div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, textAlign: 'center', marginBottom: '6px' }}>Welcome Back 👋</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', textAlign: 'center', marginBottom: '26px' }}>Login with your username and access key to access the full exam bank.</p>

            <div style={{ background: 'var(--blue-pale)', border: '1px solid var(--blue-soft)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', fontSize: '0.84rem', color: 'var(--blue)', marginBottom: '22px' }}>
              🔑 Don't have an access key? <Link to="/request?tab=access" style={{ color: 'var(--orange)', fontWeight: 700, textDecoration: 'underline' }}>Request one here →</Link>
            </div>

            <form onSubmit={doLogin}>
              <div className="form-group">
                <label className="form-label" htmlFor="login_username">Username <span className="req">*</span></label>
                <input className={`form-control${errors.username ? ' error' : ''}`} id="login_username" type="text" placeholder="e.g. raj.kumar2024" value={username} onChange={e => setUsername(e.target.value)} />
                {errors.username && <span className="form-error show">{errors.username}</span>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="login_key">Access Key <span className="req">*</span></label>
                <div style={{ position: 'relative' }}>
                  <input className={`form-control${errors.key ? ' error' : ''}`} id="login_key" type={keyVisible ? 'text' : 'password'} placeholder="Enter your access key" value={accessKey} onChange={e => setAccessKey(e.target.value)} style={{ paddingRight: '48px' }} />
                  <button type="button" onClick={() => setKeyVisible(!keyVisible)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--gray-400)' }}>
                    {keyVisible ? '🙈' : '👁'}
                  </button>
                </div>
                {errors.key && <span className="form-error show">{errors.key}</span>}
                <span className="form-hint">Your access key was sent to your email after your request was approved.</span>
              </div>
              <button type="submit" className="btn btn-blue btn-full btn-lg" style={{ marginTop: '6px' }} disabled={loading}>{loading ? 'Verifying...' : 'Login to Dashboard →'}</button>
            </form>

            <div className="divider" />
            <p style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--gray-500)' }}>
              New here? <Link to="/request?tab=access" style={{ color: 'var(--blue)', fontWeight: 700 }}>Request Exam Access</Link>
            </p>
          </div>
        </div>
      ) : (
        <div style={{ padding: '40px 5%', background: 'var(--gray-50)', minHeight: 'calc(100vh - 64px)' }}>
          <div className="container" style={{ maxWidth: '960px' }}>
            <div style={{ background: 'linear-gradient(135deg, var(--blue-pale), var(--orange-pale))', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '32px', marginBottom: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '2.4rem', marginBottom: '8px' }}>🎉</div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '6px' }}>Welcome, <span style={{ color: 'var(--blue)' }}>{dashUser}</span>!</h1>
              <p style={{ color: 'var(--gray-600)' }}>Your full exam bank is now unlocked. Happy learning!</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: '16px', marginBottom: '28px' }}>
              {[
                { icon: '📝', title: 'Full Exam Bank', desc: '200+ questions across all categories', btn: <Link to="/practice-exam" className="btn btn-blue btn-sm" style={{ marginTop: '14px' }}>Start Practice</Link> },
                { icon: '🏗️', title: 'SAP-C02 Exam', desc: 'AWS Solutions Architect Professional practice', btn: <Link to="/sap-exam" className="btn btn-orange btn-sm" style={{ marginTop: '14px' }}>Start SAP Exam</Link> },
                { icon: '🏆', title: 'My Progress', desc: `${exams.length} exam${exams.length !== 1 ? 's' : ''} completed`, btn: null },
                { icon: '🎯', title: 'Book Training', desc: '1-on-1 sessions with experts', btn: <Link to="/request?tab=session" className="btn btn-orange btn-sm" style={{ marginTop: '14px' }}>Book Now</Link> },
                { icon: '📚', title: 'Programs', desc: 'Explore full learning paths', btn: <Link to="/programs" className="btn btn-outline-gray btn-sm" style={{ marginTop: '14px' }}>View All</Link> },
              ].map((card, i) => (
                <div key={i} style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius)', padding: '24px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{card.icon}</div>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '5px' }}>{card.title}</h3>
                  <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{card.desc}</p>
                  {card.btn}
                </div>
              ))}
            </div>

            {/* Exam History */}
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '14px' }}>📊 My Exam History</h2>
              <div className="table-card">
                {exams.length === 0 ? (
                  <div className="empty-state">
                    <div className="es-icon">📭</div>
                    <p>No exams taken yet. <Link to="/practice-exam" style={{ color: 'var(--blue)', fontWeight: 700 }}>Take your first exam →</Link></p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table>
                      <thead><tr><th>Date</th><th>Category</th><th>Score</th><th>Result</th></tr></thead>
                      <tbody>
                        {exams.map((e, i) => {
                          const pct = Math.round((e.score / e.total) * 100);
                          const cls = pct >= 80 ? 'badge-green' : pct >= 60 ? 'badge-blue' : 'badge-red';
                          const label = pct >= 80 ? 'Pass' : 'Needs Work';
                          return (
                            <tr key={i}>
                              <td>{fmtDate(e._timestamp)}</td>
                              <td>{e.categoryName || e.category || '—'}</td>
                              <td><strong>{e.score}/{e.total}</strong></td>
                              <td><span className={`badge ${cls}`}>{pct}% — {label}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <button className="btn btn-outline-gray btn-sm" onClick={doLogout}>← Logout</button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default LoginPage;
